import { create } from 'zustand';

export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info' | 'pending';
  title: string;
  message: string;
  transactionHash?: string;
  timestamp: string;
  autoClose?: boolean;
  duration?: number;
  data?: any;
  isRead?: boolean;
}

// Deduplication: Track recently shown notifications to prevent duplicates
const recentNotifications = new Map<string, number>(); // key -> timestamp
const DEDUP_WINDOW_MS = 5000; // 5 second window for deduplication

/**
 * Clear the deduplication cache. Exported for testing purposes.
 */
export const clearRecentNotifications = (): void => {
  recentNotifications.clear();
};

/**
 * Generate a deduplication key from notification content
 */
const getDedupeKey = (message: string, type: string, transactionHash?: string): string => {
  // If we have a transaction hash, use that as the primary key (most reliable)
  if (transactionHash) {
    return `${type}:${transactionHash}`;
  }
  // Otherwise use message content
  return `${type}:${message}`;
};

/**
 * Check if a notification was recently shown (within dedup window)
 */
const isDuplicate = (key: string): boolean => {
  const now = Date.now();
  const lastShown = recentNotifications.get(key);

  if (lastShown && (now - lastShown) < DEDUP_WINDOW_MS) {
    console.log(`[NotificationStore] DUPLICATE BLOCKED: "${key}" was shown ${now - lastShown}ms ago`);
    return true;
  }
  return false;
};

/**
 * Record that a notification was shown
 */
const recordNotification = (key: string): void => {
  const now = Date.now();
  recentNotifications.set(key, now);

  // Clean up old entries periodically
  if (recentNotifications.size > 50) {
    for (const [k, timestamp] of recentNotifications.entries()) {
      if (now - timestamp > DEDUP_WINDOW_MS * 2) {
        recentNotifications.delete(k);
      }
    }
  }
};

interface NotificationOptions {
  type?: Notification['type'];
  title?: string;
  transactionHash?: string;
  autoClose?: boolean;
  duration?: number;
  data?: any;
}

interface NotificationState {
  notifications: Notification[];

  // Actions
  add: (message: string, options?: NotificationOptions) => string;
  remove: (id: string) => void;
  update: (id: string, updates: Partial<Notification>) => void;
  clear: () => void;
  clearByType: (type: Notification['type']) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;

  // Convenience methods
  success: (message: string, options?: Omit<NotificationOptions, 'type'>) => string;
  error: (message: string, options?: Omit<NotificationOptions, 'type'>) => string;
  warning: (message: string, options?: Omit<NotificationOptions, 'type'>) => string;
  info: (message: string, options?: Omit<NotificationOptions, 'type'>) => string;
  pending: (message: string, options?: Omit<NotificationOptions, 'type'>) => string;

  // Transaction-specific methods
  transactionPending: (hash: string, message?: string) => string;
  transactionSuccess: (hash: string, message?: string) => string;
  transactionError: (error: string, hash?: string) => string;

  // Offer-specific methods
  offerCreated: (offerId: string, hash?: string) => string;
  offerAccepted: (offerId: string, hash?: string) => string;
  offerCancelled: (offerId: string, hash?: string) => string;
  offerExpired: (offerId: string) => string;
  offerReceived: (offerData: any) => string;
}

const getDefaultTitle = (type: Notification['type']): string => {
  switch (type) {
    case 'success':
      return 'Success';
    case 'error':
      return 'Error';
    case 'warning':
      return 'Warning';
    case 'info':
      return 'Information';
    case 'pending':
      return 'Processing';
    default:
      return 'Notification';
  }
};

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],

  add: (message, options = {}) => {
    const type = options.type || 'info';

    // Check for duplicate notifications
    const dedupeKey = getDedupeKey(message, type, options.transactionHash);
    if (isDuplicate(dedupeKey)) {
      console.log(`[NotificationStore] Skipping duplicate notification: "${message}"`);
      return ''; // Return empty string to indicate no notification was added
    }

    // Record this notification to prevent future duplicates
    recordNotification(dedupeKey);

    const id = crypto.randomUUID();
    const notification: Notification = {
      id,
      type,
      title: options.title || getDefaultTitle(type),
      message,
      transactionHash: options.transactionHash,
      timestamp: new Date().toISOString(),
      autoClose: options.autoClose,
      duration: options.duration,
      data: options.data,
      isRead: false,
    };

    console.log(`[NotificationStore] Adding notification: "${message}" (type: ${type}, key: ${dedupeKey})`);

    set((state) => ({
      notifications: [notification, ...state.notifications],
    }));

    return id;
  },

  remove: (id) =>
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    })),

  update: (id, updates) =>
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, ...updates } : n
      ),
    })),

  clear: () => set({ notifications: [] }),

  clearByType: (type) =>
    set((state) => ({
      notifications: state.notifications.filter((n) => n.type !== type),
    })),

  markAsRead: (id) =>
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, isRead: true } : n
      ),
    })),

  markAllAsRead: () =>
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, isRead: true })),
    })),

  // Convenience methods
  success: (message: string, options?: Omit<NotificationOptions, 'type'>): string => {
    const state: NotificationState = useNotificationStore.getState();
    return state.add(message, { ...options, type: 'success' as const });
  },

  error: (message: string, options?: Omit<NotificationOptions, 'type'>): string => {
    const state: NotificationState = useNotificationStore.getState();
    return state.add(message, {
      ...options,
      type: 'error' as const,
      duration: 8000,
    });
  },

  warning: (message: string, options?: Omit<NotificationOptions, 'type'>): string => {
    const state: NotificationState = useNotificationStore.getState();
    return state.add(message, { ...options, type: 'warning' as const });
  },

  info: (message: string, options?: Omit<NotificationOptions, 'type'>): string => {
    const state: NotificationState = useNotificationStore.getState();
    return state.add(message, { ...options, type: 'info' as const });
  },

  pending: (message: string, options?: Omit<NotificationOptions, 'type'>): string => {
    const state: NotificationState = useNotificationStore.getState();
    return state.add(message, {
      ...options,
      type: 'pending' as const,
      autoClose: false,
    });
  },

  // Transaction methods
  transactionPending: (hash: string, message = 'Transaction submitted and pending confirmation'): string => {
    const state: NotificationState = useNotificationStore.getState();
    return state.add(message, {
      type: 'pending' as const,
      title: 'Transaction Pending',
      transactionHash: hash,
      autoClose: false,
    });
  },

  transactionSuccess: (hash: string, message = 'Transaction completed successfully'): string => {
    const state: NotificationState = useNotificationStore.getState();
    return state.add(message, {
      type: 'success' as const,
      title: 'Transaction Successful',
      transactionHash: hash,
      duration: 8000,
    });
  },

  transactionError: (error: string, hash?: string): string => {
    const state: NotificationState = useNotificationStore.getState();
    return state.add(error, {
      type: 'error' as const,
      title: 'Transaction Failed',
      transactionHash: hash,
      duration: 8000,
    });
  },

  // Offer methods
  offerCreated: (offerId: string, hash?: string): string => {
    const state: NotificationState = useNotificationStore.getState();
    return state.add('Your offer has been created successfully', {
      type: 'success' as const,
      title: 'Offer Created',
      transactionHash: hash,
      data: { offerId, action: 'offer_created' },
    });
  },

  offerAccepted: (offerId: string, hash?: string): string => {
    const state: NotificationState = useNotificationStore.getState();
    return state.add('Offer has been accepted successfully', {
      type: 'success' as const,
      title: 'Offer Accepted',
      transactionHash: hash,
      data: { offerId, action: 'offer_accepted' },
    });
  },

  offerCancelled: (offerId: string, hash?: string): string => {
    const state: NotificationState = useNotificationStore.getState();
    return state.add('Your offer has been cancelled', {
      type: 'info' as const,
      title: 'Offer Cancelled',
      transactionHash: hash,
      data: { offerId, action: 'offer_cancelled' },
    });
  },

  offerExpired: (offerId: string): string => {
    const state: NotificationState = useNotificationStore.getState();
    return state.add('Your offer has expired and is no longer active', {
      type: 'warning' as const,
      title: 'Offer Expired',
      data: { offerId, action: 'offer_expired' },
    });
  },

  offerReceived: (offerData: any): string => {
    const state: NotificationState = useNotificationStore.getState();
    return state.add(
      `New ${offerData.offer_type} offer received for ${offerData.amount} XRP`,
      {
        type: 'info' as const,
        title: 'New Offer Received',
        data: { ...offerData, action: 'offer_received' },
      }
    );
  },
}));

// Selector functions (defined outside store to avoid circular references)
export const getUnreadCount = (): number => {
  const notifications = useNotificationStore.getState().notifications;
  return notifications.filter((n) => !n.isRead).length;
};

export const getUnreadNotifications = (): Notification[] => {
  const notifications = useNotificationStore.getState().notifications;
  return notifications.filter((n) => !n.isRead);
};
